import { type Component, type JSX, Show, splitProps } from "solid-js";
import { Dynamic } from "solid-js/web";
import type { LucideProps } from "@fleet/icons";
import { cn } from "@stockpilot/shared";

import { useSwipeRow } from "@/lib/gestures/use-swipe-row";

interface SwipeAction {
  /** Icon shown in the revealed action background. */
  icon: Component<LucideProps>;
  /** Accessible label for the action (also the visible caption). */
  label: string;
  /** Handler fired when the swipe commits past the threshold. */
  onCommit: () => void;
  /** Background/foreground tone of the action panel. */
  tone?: "primary" | "danger";
  /** Whether committing slides the row fully off (e.g. delete). Default false. */
  exits?: boolean;
}

interface SwipeRowProps {
  /** Action revealed by swiping right (panel sits at the left edge). */
  right?: SwipeAction;
  /** Action revealed by swiping left (panel sits at the right edge). */
  left?: SwipeAction;
  /** Disable the gesture (still renders children normally). */
  disabled?: boolean;
  /** The row contents. Buttons/links inside stay tappable; swipe is layered. */
  children: JSX.Element;
  class?: string;
}

const TONE_CLASSES = {
  primary: "bg-primary text-primary-foreground",
  danger: "bg-destructive text-destructive-foreground",
} as const;

/** Peak tint (% of the destructive colour) a table row reaches at the commit threshold. */
const TABLE_ROW_TINT_PEAK_PCT = 22;

function ActionPanel(props: { action: SwipeAction; side: "left" | "right"; visible: boolean }) {
  return (
    <div
      aria-hidden="true"
      class={cn(
        "absolute inset-y-0 flex w-28 items-center gap-2 px-4 text-sm font-medium transition-opacity duration-150",
        props.side === "left" ? "left-0 justify-start" : "right-0 justify-end",
        TONE_CLASSES[props.action.tone ?? "primary"],
        props.visible ? "opacity-100" : "opacity-0",
      )}
    >
      <Dynamic component={props.action.icon} class="h-5 w-5 shrink-0" />
      <span class="truncate">{props.action.label}</span>
    </div>
  );
}

interface SwipeTableRowProps extends JSX.HTMLAttributes<HTMLTableRowElement> {
  /** Left-swipe action (revealed at the right edge), e.g. delete a line. */
  left?: SwipeAction;
  /** Disable the gesture (still renders children normally). */
  disabled?: boolean;
  /** The `<td>` cells of the row. */
  children: JSX.Element;
}

/**
 * Table-row counterpart of {@link SwipeRow} for lists that are rendered as a
 * real `<table>` (count lines, lots, …). A `<tr>` cannot host an absolutely
 * positioned reveal panel without breaking table layout, so instead the row
 * itself is the swipe surface: it translates on the X axis and tints toward the
 * action tone as it nears the commit threshold, reusing the exact same
 * {@link useSwipeRow} physics (axis-lock, rubber-band, rising-edge haptic,
 * `prefers-reduced-motion`). The inner buttons/keyboard path stay intact.
 */
export function SwipeTableRow(props: SwipeTableRowProps) {
  let surfaceRef: HTMLTableRowElement | undefined = undefined;
  const [local, rest] = splitProps(props, ["left", "disabled", "children", "class"]);

  const swipe = useSwipeRow({
    getSurfaceRef: () => surfaceRef,
    canStart: () => !local.disabled,
    getRight: () => undefined,
    getLeft: () => {
      const action = local.left;
      return action ? { onCommit: action.onCommit, exits: action.exits ?? false } : undefined;
    },
  });

  // Progress (0→1) toward the commit threshold while swiping left.
  const progress = () => Math.min(1, Math.max(0, -swipe.tx() / swipe.commitThresholdPx));

  return (
    <tr
      {...rest}
      ref={(el) => {
        surfaceRef = el;
      }}
      class={cn("touch-pan-y", local.class)}
      style={{
        transform: `translate3d(${swipe.tx()}px, 0, 0)`,
        "background-color":
          progress() > 0
            ? `color-mix(in srgb, hsl(var(--destructive)) ${Math.round(progress() * TABLE_ROW_TINT_PEAK_PCT)}%, transparent)`
            : undefined,
        transition: swipe.isAnimating() ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)" : "none",
      }}
      onPointerDown={swipe.handlePointerDown}
      onPointerMove={swipe.handlePointerMove}
      onPointerUp={swipe.handlePointerUp}
      onPointerCancel={swipe.handlePointerCancel}
    >
      {local.children}
    </tr>
  );
}

/**
 * A list-row wrapper that adds premium swipe-to-action on touch while leaving
 * the inner controls (buttons / links) and keyboard a11y fully intact — the
 * swipe is a progressive enhancement, never the only path to an action.
 *
 * Swipe right → `right` action; swipe left → `left` action. The recognizer
 * locks to the X axis so it never fights vertical scroll, pulses a haptic at the
 * commit threshold, and honours `prefers-reduced-motion`.
 */
export function SwipeRow(props: SwipeRowProps) {
  let surfaceRef: HTMLDivElement | undefined = undefined;

  const swipe = useSwipeRow({
    getSurfaceRef: () => surfaceRef,
    canStart: () => !props.disabled,
    getRight: () => {
      const action = props.right;
      return action ? { onCommit: action.onCommit, exits: action.exits ?? false } : undefined;
    },
    getLeft: () => {
      const action = props.left;
      return action ? { onCommit: action.onCommit, exits: action.exits ?? false } : undefined;
    },
  });

  return (
    <div class={cn("relative overflow-hidden", props.class)}>
      <Show when={props.right}>
        {(action) => <ActionPanel action={action()} side="left" visible={swipe.tx() > 0} />}
      </Show>
      <Show when={props.left}>
        {(action) => <ActionPanel action={action()} side="right" visible={swipe.tx() < 0} />}
      </Show>

      <div
        ref={(el) => {
          surfaceRef = el;
        }}
        class="relative z-10 bg-card touch-pan-y"
        style={{
          transform: `translate3d(${swipe.tx()}px, 0, 0)`,
          transition: swipe.isAnimating()
            ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)"
            : "none",
        }}
        onPointerDown={swipe.handlePointerDown}
        onPointerMove={swipe.handlePointerMove}
        onPointerUp={swipe.handlePointerUp}
        onPointerCancel={swipe.handlePointerCancel}
      >
        {props.children}
      </div>
    </div>
  );
}
